import { Link } from 'react-router';
import { Users, Activity, TrendingUp, ArrowRight } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { SkeletonCard } from '@/components/ui/Skeleton';
import { PatientCard } from '@/components/features/patients/PatientCard';
import { usePatients } from '@/hooks/usePatients';
import { useAuth } from '@/hooks/useAuth';
import { getUserDisplayName } from '@/types/auth';

export function DoctorDashboard() {
  const { user, isProfileLoading, profileError, refreshProfile } = useAuth();
  const { patients, isLoading } = usePatients();
  const displayName = getUserDisplayName(user);

  const activePatients = patients.filter((p) => p.status === 'active');
  const totalActivities = patients.reduce((sum, p) => sum + p.totalActivities, 0);
  // Show patients with most activities as "recent"
  const recentPatients = patients
    .filter((p) => p.totalActivities > 0)
    .sort((a, b) => b.totalActivities - a.totalActivities)
    .slice(0, 3);

  const stats = [
    {
      label: 'Total Patients',
      value: patients.length,
      icon: Users,
      color: 'bg-primary-100 text-primary',
    },
    {
      label: 'Active Patients',
      value: activePatients.length,
      icon: TrendingUp,
      color: 'bg-secondary-100 text-secondary',
    },
    {
      label: 'Total Activities',
      value: totalActivities,
      icon: Activity,
      color: 'bg-purple-100 text-purple-600',
    },
  ];

  return (
    <div>
      <PageHeader
        title={`Welcome back, ${displayName}`}
        description={
          profileError
            ? 'Doctor profile could not be loaded. Showing your current session details.'
            : isProfileLoading
              ? 'Loading your profile...'
              : "Here's an overview of your patient data"
        }
        actions={
          profileError ? (
            <Button variant="outline" size="sm" onClick={() => void refreshProfile()}>
              Retry profile
            </Button>
          ) : undefined
        }
      />

      {/* Stats */}
      <div className="mb-8 grid gap-4 sm:grid-cols-3">
        {isLoading
          ? Array.from({ length: 3 }).map((_, i) => (
              <SkeletonCard key={i} className="h-28" />
            ))
          : stats.map((stat) => (
              <Card key={stat.label}>
                <CardBody className="flex items-center gap-4">
                  <div
                    className={`flex h-12 w-12 items-center justify-center rounded-lg ${stat.color}`}
                  >
                    <stat.icon className="h-6 w-6" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-foreground">
                      {stat.value.toLocaleString()}
                    </p>
                    <p className="text-sm text-muted">{stat.label}</p>
                  </div>
                </CardBody>
              </Card>
            ))}
      </div>

      {/* Recent Patients */}
      <Card>
        <CardHeader
          title="Recent Activity"
          description="Patients with recent activities"
          action={
            <Link to="/doctor/patients">
              <Button variant="ghost" size="sm">
                View all
                <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
            </Link>
          }
        />
        <CardBody>
          {isLoading ? (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <SkeletonCard key={i} />
              ))}
            </div>
          ) : recentPatients.length === 0 ? (
            <p className="py-8 text-center text-muted">
              No recent patient activity
            </p>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {recentPatients.map((patient) => (
                <PatientCard key={patient.id} patient={patient} />
              ))}
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
